import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Header } from "./Header.tsx";
import { PrimaryNavigation, type ViewType } from "./PrimaryNavigation.tsx";
import { useNavigationState } from "../../hooks/useNavigationState.ts";
import { OverviewView } from "../views/OverviewView.tsx";
import { RequestsView } from "../views/RequestsView.tsx";
import { MOCK_REQUESTS } from "../../data/mockRequests.ts";
import {
  filterByTimeRange,
  computeMetrics,
  generateChartBuckets,
  computeRecentSecurityEvents,
  INSPECTION_FAILURE_STATUSES,
} from "../../utils/aggregation.ts";
import { filterRequests, sortRequests, paginateRequests } from "../../utils/filters.ts";
import type {
  TimeRangeKey,
  ShortcutFilter,
  DecisionFilter,
  InspectionFilter,
  SortField,
  SortOrder,
  BundleQueryParams,
  SidebarCounts,
  RequestRecord,
  WebsiteRecord,
} from "../../types/telemetry.ts";
import { useTelemetryPolling } from "../../hooks/useTelemetryPolling.ts";
import { useManagementHealth } from "../../hooks/useManagementHealth.ts";
import { useRequestDetail } from "../../hooks/useRequestDetail.ts";
import { Shield, Loader2, AlertCircle } from "lucide-react";
import { AuthProvider, useAuth } from "../../hooks/useAuth.tsx";
import { AuthScreen } from "../auth/AuthScreen.tsx";
import { fetchWebsites, ApiClientError } from "../../services/apiClient.ts";
import { NoWebsitesEmptyState } from "../websites/NoWebsitesEmptyState.tsx";
import { CustomerDashboard } from "./CustomerDashboard.tsx";
import { OnboardingWizard } from "../onboarding/OnboardingWizard.tsx";

interface LiveShellProps {
  onSwitchToCustomer?: () => void;
}

/**
 * LiveShell: Connected container for the live management API.
 * Uses useTelemetryPolling, useManagementHealth, and useRequestDetail.
 * Uses explicit nulls for unavailable data during initial loading or outage.
 */
const LiveShell: React.FC<LiveShellProps> = ({ onSwitchToCustomer }) => {
  const [activeView, setActiveView] = useState<ViewType>("overview");
  const {
    isPinned,
    togglePinned,
    isMobileOpen,
    openMobile,
    closeMobile,
  } = useNavigationState();

  // Shared authoritative query state across Overview and Requests
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("60m");
  const [shortcut, setShortcut] = useState<ShortcutFilter>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("ALL");
  const [inspectionFilter, setInspectionFilter] = useState<InspectionFilter>("ALL");
  const [sortField, setSortField] = useState<SortField>("timestamp");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Selection state for request inspection drawer
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  // Debounce search input by 300ms to avoid flooding requests while typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset page to 1 whenever filters, sorting, time-range, or page-size change
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, [
    timeRange,
    shortcut,
    debouncedSearch,
    decisionFilter,
    inspectionFilter,
    sortField,
    sortOrder,
    pageSize,
  ]);

  // Authoritative query params matching backend schema (explicitly demo-endpoint scoped)
  const queryParams: BundleQueryParams = useMemo(
    () => ({
      demoEndpoint: true,
      timeRange,
      shortcut,
      search: debouncedSearch.trim() || undefined,
      decision: decisionFilter,
      inspectionStatus: inspectionFilter,
      sortField,
      sortOrder,
      page,
      pageSize,
    }),
    [
      timeRange,
      shortcut,
      debouncedSearch,
      decisionFilter,
      inspectionFilter,
      sortField,
      sortOrder,
      page,
      pageSize,
    ]
  );

  // Live Management API Hooks
  const livePolling = useTelemetryPolling(queryParams, { enabled: true });
  const liveHealth = useManagementHealth({ autoPoll: true, enabled: true });
  const liveDetail = useRequestDetail(selectedRequestId, null, true);

  // Authoritative server-provided data or explicit nulls during outage
  const summary = livePolling.data?.summary ?? null;
  const chart = livePolling.data?.chart ?? null;
  const sidebarCounts: SidebarCounts | null = livePolling.data?.sidebarCounts ?? null;
  const tableItems: RequestRecord[] = livePolling.data?.table.items ?? [];
  const tableTotal: number | null = livePolling.data?.table.total ?? null;
  const totalPages: number | null = livePolling.data?.table.totalPages ?? null;
  const serverTime = livePolling.serverTime;

  const isLoading = livePolling.isLoading;
  const isRefreshing = livePolling.isRefreshing;
  const isStale = livePolling.isStale;
  const hasNewData = livePolling.hasNewData;
  const pollingError = livePolling.error;

  const handleRefresh = useCallback(async (): Promise<boolean> => {
    return await livePolling.refresh();
  }, [livePolling]);

  return (
    <div className="min-h-screen bg-canvas flex flex-col font-sans text-foreground">
      {/* 48px Global Top Header */}
      <Header
        isSyntheticMode={false}
        isDemoMode={true}
        serverTime={serverTime}
        onOpenMobileNav={openMobile}
        onSwitchToCustomer={onSwitchToCustomer}
        health={{
          isHealthy: liveHealth.isHealthy,
          isConnected: liveHealth.isConnected,
          status: liveHealth.status,
          database: liveHealth.database,
          isLoading: liveHealth.isLoading,
          error: liveHealth.error,
        }}
      />

      {/* Main Container below Header */}
      <div className="flex flex-grow h-[calc(100vh-48px)] overflow-hidden">
        {/* Collapsible Primary Navigation */}
        <PrimaryNavigation
          activeView={activeView}
          onSelectView={setActiveView}
          isPinned={isPinned}
          onTogglePinned={togglePinned}
          isMobileOpen={isMobileOpen}
          onCloseMobile={closeMobile}
        />

        {/* Dynamic Main Workspace Canvas */}
        <main className="flex-grow min-w-0 overflow-auto bg-canvas flex flex-col">
          {activeView === "overview" ? (
            <OverviewView
              summary={summary}
              chart={chart}
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
              isStale={isStale}
              serverTime={serverTime}
              error={pollingError}
              onRefresh={handleRefresh}
              isSyntheticMode={false}
              recentSecurityEvents={livePolling.data?.recentSecurityEvents ?? []}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={liveDetail.request}
              detailLoading={liveDetail.isLoading}
              detailError={liveDetail.error}
              onDetailRetry={liveDetail.refetch}
            />
          ) : (
            <RequestsView
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              shortcut={shortcut}
              onSelectShortcut={setShortcut}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              decisionFilter={decisionFilter}
              onDecisionChange={setDecisionFilter}
              inspectionFilter={inspectionFilter}
              onInspectionChange={setInspectionFilter}
              sortField={sortField}
              onSortFieldChange={setSortField}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              items={tableItems}
              tableTotal={tableTotal}
              totalPages={totalPages}
              sidebarCounts={sidebarCounts}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={liveDetail.request}
              detailLoading={liveDetail.isLoading}
              detailError={liveDetail.error}
              onDetailRetry={liveDetail.refetch}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
              isStale={isStale}
              hasNewData={hasNewData}
              serverTime={serverTime}
              error={pollingError}
              onRefresh={handleRefresh}
              onApplyPendingData={livePolling.applyPendingData}
              isSyntheticMode={false}
            />
          )}
        </main>
      </div>
    </div>
  );
};

/**
 * SyntheticShell: Isolated container for ?mode=synthetic.
 * Uses ZERO API calls (no useTelemetryPolling, no useManagementHealth, no useRequestDetail).
 * Computes all aggregations, filtering, sorting, pagination, and drawer lookups locally from MOCK_REQUESTS.
 */
const SyntheticShell: React.FC = () => {
  const [activeView, setActiveView] = useState<ViewType>("overview");
  const {
    isPinned,
    togglePinned,
    isMobileOpen,
    openMobile,
    closeMobile,
  } = useNavigationState();

  // Local authoritative query state
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("60m");
  const [shortcut, setShortcut] = useState<ShortcutFilter>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("ALL");
  const [inspectionFilter, setInspectionFilter] = useState<InspectionFilter>("ALL");
  const [sortField, setSortField] = useState<SortField>("timestamp");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Selection state for local request inspection drawer
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, [
    timeRange,
    shortcut,
    debouncedSearch,
    decisionFilter,
    inspectionFilter,
    sortField,
    sortOrder,
    pageSize,
  ]);

  // Compute local synthetic bundle from fixtures
  const syntheticBundle = useMemo(() => {
    const timeFiltered = filterByTimeRange(MOCK_REQUESTS, timeRange);
    const summary = computeMetrics(timeFiltered);
    const buckets = generateChartBuckets(timeFiltered, timeRange);
    const chart = {
      bucketSeconds: 60,
      buckets,
    };

    let blocked = 0;
    let flagged = 0;
    let failures = 0;
    for (const r of timeFiltered) {
      if (r.policy_decision === "BLOCK") blocked++;
      if (r.policy_decision === "FLAG") flagged++;
      if (INSPECTION_FAILURE_STATUSES.has(r.inspection_status)) failures++;
    }
    const sidebarCounts: SidebarCounts = {
      all: timeFiltered.length,
      blocked,
      flagged,
      failures,
    };

    const filtered = filterRequests(timeFiltered, {
      shortcut,
      searchQuery: debouncedSearch,
      decisionFilter,
      inspectionFilter,
    });
    const sorted = sortRequests(filtered, sortField, sortOrder);
    const paginated = paginateRequests(sorted, page, pageSize);

    const recentSecurityEvents = computeRecentSecurityEvents(timeFiltered);

    return {
      summary,
      chart,
      sidebarCounts,
      tableItems: paginated.items,
      tableTotal: sorted.length,
      totalPages: paginated.totalPages,
      serverTime: "2026-10-04T15:30:00.000000Z",
      recentSecurityEvents,
      decisionAttribution: summary.decisionAttribution,
      recordedModes: summary.recordedModes,
      aiSummary: {
        aiEvaluatedCount: summary.aiEvaluatedCount,
        aiNotEvaluatedCount: summary.aiNotEvaluatedCount,
        aiSkippedCount: summary.aiSkippedCount,
        aiUnreliableRedactionCount: summary.aiUnreliableRedactionCount,
        aiFailureCount: summary.aiFailureCount,
        aiOtherStatusCount: summary.aiOtherStatusCount,
        aiCoveragePercent: summary.aiCoveragePercent,
        avgAiRoundtripMs: summary.avgAiRoundtripMs,
      },
    };
  }, [
    timeRange,
    shortcut,
    debouncedSearch,
    decisionFilter,
    inspectionFilter,
    sortField,
    sortOrder,
    page,
    pageSize,
  ]);

  const handleRefresh = useCallback(async (): Promise<boolean> => {
    // Local refresh (no-op network call)
    return true;
  }, []);

  const detailRequest: RequestRecord | null = useMemo(() => {
    if (!selectedRequestId) return null;
    return MOCK_REQUESTS.find((r) => r.id === selectedRequestId) ?? null;
  }, [selectedRequestId]);

  return (
    <div className="min-h-screen bg-canvas flex flex-col font-sans text-foreground">
      {/* 48px Global Top Header with Synthetic Mode badge, no health connection claim */}
      <Header
        isSyntheticMode={true}
        serverTime={syntheticBundle.serverTime}
        health={undefined}
        onOpenMobileNav={openMobile}
      />

      {/* Main Container below Header */}
      <div className="flex flex-grow h-[calc(100vh-48px)] overflow-hidden">
        <PrimaryNavigation
          activeView={activeView}
          onSelectView={setActiveView}
          isPinned={isPinned}
          onTogglePinned={togglePinned}
          isMobileOpen={isMobileOpen}
          onCloseMobile={closeMobile}
        />

        <main className="flex-grow min-w-0 overflow-auto bg-canvas flex flex-col">
          {activeView === "overview" ? (
            <OverviewView
              summary={syntheticBundle.summary}
              chart={syntheticBundle.chart}
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              isLoading={false}
              isRefreshing={false}
              isStale={false}
              serverTime={syntheticBundle.serverTime}
              error={null}
              onRefresh={handleRefresh}
              isSyntheticMode={true}
              recentSecurityEvents={syntheticBundle.recentSecurityEvents}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={detailRequest}
              detailLoading={false}
              detailError={
                selectedRequestId && !detailRequest
                  ? new Error("Request record not found in synthetic fixtures")
                  : null
              }
              onDetailRetry={() => {}}
            />
          ) : (
            <RequestsView
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              shortcut={shortcut}
              onSelectShortcut={setShortcut}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              decisionFilter={decisionFilter}
              onDecisionChange={setDecisionFilter}
              inspectionFilter={inspectionFilter}
              onInspectionChange={setInspectionFilter}
              sortField={sortField}
              onSortFieldChange={setSortField}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              items={syntheticBundle.tableItems}
              tableTotal={syntheticBundle.tableTotal}
              totalPages={syntheticBundle.totalPages}
              sidebarCounts={syntheticBundle.sidebarCounts}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={detailRequest}
              detailLoading={false}
              detailError={
                selectedRequestId && !detailRequest
                  ? new Error("Request record not found in synthetic fixtures")
                  : null
              }
              isLoading={false}
              isRefreshing={false}
              isStale={false}
              hasNewData={false}
              serverTime={syntheticBundle.serverTime}
              error={null}
              onRefresh={handleRefresh}
              onApplyPendingData={() => {}}
              isSyntheticMode={true}
            />
          )}
        </main>
      </div>
    </div>
  );
};

const CONTROLLED_FIXTURE_WEBSITES: WebsiteRecord[] = [
  {
    id: "aaaaaaaa-1111-4111-8111-aaaaaaaaaaaa",
    account_id: "99999999-9999-4999-8999-999999999999",
    display_name: "Production Gateway",
    public_hostname: "api.customer.com",
    origin_url: "https://origin.customer.internal",
    origin_host_header: null,
    onboarding_status: "ACTIVE",
    policy_mode: "BLOCKING",
    ai_mode: "SHADOW",
    tls_status: "PROVISIONED",
    is_development: false,
    verification_token: null,
    dns_txt_record_name: null,
    created_at: "2026-10-06T10:00:00Z",
    updated_at: "2026-10-06T10:00:00Z",
    verified_at: "2026-10-06T10:05:00Z",
    activated_at: "2026-10-06T10:10:00Z",
  },
  {
    id: "bbbbbbbb-2222-4222-8222-bbbbbbbbbbbb",
    account_id: "99999999-9999-4999-8999-999999999999",
    display_name: "Staging Store",
    public_hostname: "shop-staging.customer.com",
    origin_url: "https://staging.customer.internal",
    origin_host_header: null,
    onboarding_status: "DRAFT",
    policy_mode: "BLOCKING",
    ai_mode: "SHADOW",
    tls_status: "NOT_PROVISIONED",
    is_development: false,
    verification_token: "tok_staging_abc123xyz456",
    dns_txt_record_name: "_aishield-challenge.shop-staging.customer.com",
    created_at: "2026-10-07T14:30:00Z",
    updated_at: "2026-10-07T14:30:00Z",
    verified_at: null,
    activated_at: null,
    dns_instructions: {
      record_type: "TXT",
      record_name: "_aishield-challenge.shop-staging.customer.com",
      expected_value: "aishield-verify=tok_staging_abc123xyz456",
      description: "Create a DNS TXT record at '_aishield-challenge.shop-staging.customer.com' with value 'aishield-verify=tok_staging_abc123xyz456'.",
    },
  },
  {
    id: "cccccccc-3333-4333-8333-cccccccccccc",
    account_id: "99999999-9999-4999-8999-999999999999",
    display_name: "Partner API",
    public_hostname: "partners.customer.com",
    origin_url: "https://partners.customer.internal",
    origin_host_header: null,
    onboarding_status: "PENDING_OWNERSHIP",
    policy_mode: "BLOCKING",
    ai_mode: "SHADOW",
    tls_status: "NOT_PROVISIONED",
    is_development: false,
    verification_token: "tok_partners_def789uvw012",
    dns_txt_record_name: "_aishield-challenge.partners.customer.com",
    created_at: "2026-10-08T01:00:00Z",
    updated_at: "2026-10-08T01:00:00Z",
    verified_at: null,
    activated_at: null,
    dns_instructions: {
      record_type: "TXT",
      record_name: "_aishield-challenge.partners.customer.com",
      expected_value: "aishield-verify=tok_partners_def789uvw012",
      description: "Create a DNS TXT record at '_aishield-challenge.partners.customer.com' with value 'aishield-verify=tok_partners_def789uvw012'.",
    },
  },
  {
    id: "dddddddd-4444-4444-8444-dddddddddddd",
    account_id: "99999999-9999-4999-8999-999999999999",
    display_name: "Docs Portal",
    public_hostname: "docs.customer.com",
    origin_url: "https://docs.customer.internal",
    origin_host_header: null,
    onboarding_status: "OWNERSHIP_VERIFIED",
    policy_mode: "BLOCKING",
    ai_mode: "SHADOW",
    tls_status: "NOT_PROVISIONED",
    is_development: false,
    verification_token: "tok_docs_verified_4444",
    dns_txt_record_name: "_aishield-challenge.docs.customer.com",
    created_at: "2026-10-08T02:00:00Z",
    updated_at: "2026-10-08T02:00:00Z",
    verified_at: "2026-10-08T02:05:00Z",
    activated_at: null,
    dns_instructions: {
      record_type: "TXT",
      record_name: "_aishield-challenge.docs.customer.com",
      expected_value: "aishield-verify=tok_docs_verified_4444",
      description: "Create a DNS TXT record at '_aishield-challenge.docs.customer.com' with value 'aishield-verify=tok_docs_verified_4444'.",
    },
  },
  {
    id: "eeeeeeee-5555-4555-8555-eeeeeeeeeeee",
    account_id: "99999999-9999-4999-8999-999999999999",
    display_name: "Analytics Service",
    public_hostname: "analytics.customer.com",
    origin_url: "https://analytics.customer.internal",
    origin_host_header: null,
    onboarding_status: "ROUTING_READY",
    policy_mode: "BLOCKING",
    ai_mode: "SHADOW",
    tls_status: "NOT_PROVISIONED",
    is_development: false,
    verification_token: "tok_analytics_5555",
    dns_txt_record_name: "_aishield-challenge.analytics.customer.com",
    created_at: "2026-10-08T02:10:00Z",
    updated_at: "2026-10-08T02:15:00Z",
    verified_at: "2026-10-08T02:12:00Z",
    activated_at: null,
  },
];

const CustomerPortalContent: React.FC<{ onSwitchToDemo: () => void }> = ({
  onSwitchToDemo,
}) => {
  const { status, error, checkSession, logout } = useAuth();
  const [websites, setWebsites] = useState<WebsiteRecord[]>([]);
  const [websitesLoading, setWebsitesLoading] = useState<boolean>(true);
  const [websitesError, setWebsitesError] = useState<string | null>(null);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string | null>(null);

  const fixtureMode = useMemo(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("fixture");
  }, []);

  const [isWizardOpen, setIsWizardOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const fx = params.get("fixture");
    return Boolean(fx && fx.startsWith("onboarding"));
  });

  const [wizardWebsite, setWizardWebsite] = useState<WebsiteRecord | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const fx = params.get("fixture");
    if (fx === "onboarding-step2") return CONTROLLED_FIXTURE_WEBSITES[1];
    if (fx === "onboarding-step3-unconfigured" || fx === "onboarding-step3-ready") return CONTROLLED_FIXTURE_WEBSITES[3];
    if (fx === "onboarding-step4") return CONTROLLED_FIXTURE_WEBSITES[4];
    return null;
  });

  const handleOpenAddWebsite = useCallback(() => {
    setWizardWebsite(null);
    setIsWizardOpen(true);
  }, []);

  const handleResumeSetup = useCallback((site: WebsiteRecord) => {
    setWizardWebsite(site);
    setIsWizardOpen(true);
  }, []);

  const handleCloseWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  const handleWebsiteCreated = useCallback((newSite: WebsiteRecord) => {
    setWebsites((prev) => [newSite, ...prev]);
    setSelectedWebsiteId(newSite.id);
  }, []);

  const handleWebsiteUpdated = useCallback((updatedSite: WebsiteRecord) => {
    setWebsites((prev) =>
      prev.map((w) => (w.id === updatedSite.id ? updatedSite : w))
    );
  }, []);

  const loadWebsites = useCallback(async () => {
    if (fixtureMode === "empty") {
      setWebsites([]);
      setSelectedWebsiteId(null);
      setWebsitesLoading(false);
      return;
    }
    if (fixtureMode && fixtureMode.startsWith("onboarding")) {
      setWebsites(CONTROLLED_FIXTURE_WEBSITES);
      setSelectedWebsiteId(CONTROLLED_FIXTURE_WEBSITES[0].id);
      setWebsitesLoading(false);
      return;
    }
    if (fixtureMode === "websites") {
      setWebsites(CONTROLLED_FIXTURE_WEBSITES);
      setSelectedWebsiteId(CONTROLLED_FIXTURE_WEBSITES[0].id);
      setWebsitesLoading(false);
      return;
    }

    setWebsitesLoading(true);
    setWebsitesError(null);
    try {
      const res = await fetchWebsites();
      setWebsites(res.items);
      if (res.items.length > 0) {
        setSelectedWebsiteId((prev) => {
          if (prev && res.items.some((w) => w.id === prev)) return prev;
          return res.items[0].id;
        });
      } else {
        setSelectedWebsiteId(null);
      }
    } catch (err: unknown) {
      if (err instanceof ApiClientError && err.status === 401) {
        await logout();
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to load registered websites";
      setWebsitesError(msg);
    } finally {
      setWebsitesLoading(false);
    }
  }, [fixtureMode, logout]);

  const handleWizardFinish = useCallback((finishedSite: WebsiteRecord) => {
    setSelectedWebsiteId(finishedSite.id);
    setIsWizardOpen(false);
    loadWebsites();
  }, [loadWebsites]);

  useEffect(() => {
    if (status === "authenticated" || fixtureMode) {
      loadWebsites();
    } else {
      setWebsites([]);
      setSelectedWebsiteId(null);
      setWebsitesLoading(false);
      setWebsitesError(null);
    }
  }, [status, fixtureMode, loadWebsites]);

  if (status === "checking" || status === "idle") {
    return (
      <div className="min-h-screen bg-canvas flex flex-col justify-center items-center text-foreground font-sans select-none">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-subtle text-brand border border-brand-border flex items-center justify-center animate-pulse">
            <Shield className="w-6 h-6" />
          </div>
          <div className="flex items-center gap-2 text-xs font-mono text-foreground-muted">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Verifying customer session...</span>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-canvas flex flex-col justify-center items-center p-4 text-foreground font-sans select-none">
        <div className="w-full max-w-md bg-panel border border-border rounded-lg p-6 sm:p-8 text-center shadow-sm">
          <div className="w-10 h-10 rounded-lg bg-verdict-block/10 text-verdict-block border border-verdict-block/30 flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="w-5 h-5" />
          </div>
          <h2 className="text-sm font-semibold text-foreground mb-1">
            Authentication Service Unavailable
          </h2>
          <p className="text-xs text-foreground-muted mb-5 leading-relaxed">
            {error || "Unable to reach the AIShield management service."}
          </p>
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => checkSession()}
              className="w-full h-8 text-xs font-mono font-medium rounded bg-brand hover:bg-brand-hover text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
            >
              Retry Connection
            </button>
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={onSwitchToDemo}
                className="w-full h-8 text-xs font-mono rounded bg-canvas hover:bg-panel-hover border border-border text-foreground-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border"
              >
                Explore Local Demo Console
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === "authenticated" || fixtureMode) {
    if (websitesLoading) {
      return (
        <div className="min-h-screen bg-canvas flex flex-col justify-center items-center text-foreground font-sans select-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-subtle text-brand border border-brand-border flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
            <div className="text-xs font-mono text-foreground-muted">
              Loading registered websites...
            </div>
          </div>
        </div>
      );
    }

    if (websitesError) {
      return (
        <div className="min-h-screen bg-canvas flex flex-col justify-center items-center p-4 text-foreground font-sans select-none">
          <div className="w-full max-w-md bg-panel border border-border rounded-lg p-6 sm:p-8 text-center shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-verdict-block/10 text-verdict-block border border-verdict-block/30 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h2 className="text-sm font-semibold text-foreground mb-1">
              Unable to Load Websites
            </h2>
            <p className="text-xs text-foreground-muted mb-5 leading-relaxed">
              {websitesError}
            </p>
            <button
              type="button"
              onClick={loadWebsites}
              className="w-full h-8 text-xs font-mono font-medium rounded bg-brand hover:bg-brand-hover text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const selectedSite =
      websites.find((w) => w.id === selectedWebsiteId) || websites[0] || null;

    return (
      <>
        {websites.length === 0 ? (
          <NoWebsitesEmptyState
            onAddWebsite={handleOpenAddWebsite}
            onSwitchToDemo={onSwitchToDemo}
            onRefreshWebsites={loadWebsites}
          />
        ) : (
          <CustomerDashboard
            websites={websites}
            selectedWebsite={selectedSite}
            onSelectWebsite={(site) => setSelectedWebsiteId(site.id)}
            onAddWebsite={handleOpenAddWebsite}
            onResumeSetup={handleResumeSetup}
            onSwitchToDemo={onSwitchToDemo}
          />
        )}

        <OnboardingWizard
          isOpen={isWizardOpen}
          initialWebsite={wizardWebsite}
          fixtureMode={fixtureMode}
          onClose={handleCloseWizard}
          onWebsiteCreated={handleWebsiteCreated}
          onWebsiteUpdated={handleWebsiteUpdated}
          onFinish={handleWizardFinish}
        />
      </>
    );
  }

  return <AuthScreen onSwitchToDemo={onSwitchToDemo} />;
};

const CustomerPortal: React.FC<{ onSwitchToDemo: () => void }> = ({
  onSwitchToDemo,
}) => {
  return (
    <AuthProvider>
      <CustomerPortalContent onSwitchToDemo={onSwitchToDemo} />
    </AuthProvider>
  );
};

export const Shell: React.FC = () => {
  const [currentMode, setCurrentMode] = useState<string>(() => {
    if (typeof window === "undefined") return "customer";
    const params = new URLSearchParams(window.location.search);
    return params.get("mode") || "customer";
  });

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      setCurrentMode(params.get("mode") || "customer");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleSwitchToDemo = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("mode", "demo");
    window.history.pushState({}, "", url.toString());
    setCurrentMode("demo");
  }, []);

  const handleSwitchToCustomer = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("mode");
    window.history.pushState({}, "", url.toString());
    setCurrentMode("customer");
  }, []);

  if (currentMode === "synthetic") {
    return <SyntheticShell />;
  }

  if (currentMode === "demo" || currentMode === "shieldstore") {
    // Hide demo mode in production
    if (!import.meta.env.DEV) {
      return <CustomerPortal onSwitchToDemo={handleSwitchToDemo} />;
    }
    return <LiveShell onSwitchToCustomer={handleSwitchToCustomer} />;
  }

  return <CustomerPortal onSwitchToDemo={handleSwitchToDemo} />;
};

